import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import axios from 'axios'
import ImageUploader from './ImageUploader'

type PromptResult =
  | { prompt: string; status: 'success'; metadata?: any }
  | { prompt: string; status: 'error'; error?: string }

function parsePrompts(raw: string): string[] {
  const parts = (raw || '')
    .split(/[\n,]+/g)
    .map((p) => p.trim())
    .filter(Boolean)
  const unique: string[] = []
  for (const p of parts) {
    if (!unique.includes(p)) unique.push(p)
  }
  return unique
}

function normalizeDataUrlToB64(dataUrlOrB64: string): string {
  if (!dataUrlOrB64) return ''
  const idx = dataUrlOrB64.indexOf(',')
  return idx >= 0 ? dataUrlOrB64.slice(idx + 1) : dataUrlOrB64
}

function toDataUrlPng(b64: string): string {
  return `data:image/png;base64,${b64}`
}

type Mode = 'lingerie-cover' | 'keep-only' | 'rectangles'

function defaultPromptsForMode(mode: Mode): string {
  if (mode === 'keep-only') return 'face\nhead\nhands\nfeet'
  return 'bra\npanties\ntorso'
}

type RectDef = { id: string; x: number; y: number; w: number; h: number; unit: 'norm' }

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function clamp01(value: number): number {
  return clamp(value, 0, 1)
}

function defaultRectangles(count: number): RectDef[] {
  const c = clamp(Math.floor(count || 0), 0, 3)
  const rects: RectDef[] = []
  if (c >= 1) rects.push({ id: 'rect-1', x: 0.32, y: 0.34, w: 0.36, h: 0.12, unit: 'norm' }) // bra-ish
  if (c >= 2) rects.push({ id: 'rect-2', x: 0.34, y: 0.62, w: 0.32, h: 0.14, unit: 'norm' }) // panties-ish
  if (c >= 3) rects.push({ id: 'rect-3', x: 0.30, y: 0.48, w: 0.40, h: 0.12, unit: 'norm' }) // torso fallback
  return rects
}

const LingerieFixTool: React.FC = () => {
  const [mode, setMode] = useState<Mode>('lingerie-cover')
  const [originalImage, setOriginalImage] = useState<string>('')
  const [fixedImage, setFixedImage] = useState<string>('')

  const [promptsText, setPromptsText] = useState<string>(defaultPromptsForMode('lingerie-cover'))
  const prompts = useMemo(() => parsePrompts(promptsText), [promptsText])

  const [allowPartial, setAllowPartial] = useState<boolean>(true)
  const [alpha, setAlpha] = useState<number>(255)
  const [color, setColor] = useState<string>('#000000')
  const defaultMaskByMode: Record<Mode, { expand: number; feather: number }> = {
    'lingerie-cover': { expand: 2, feather: 1 },
    'keep-only': { expand: 2, feather: 1 },
    'rectangles': { expand: 10, feather: 3 }
  }
  const [maskByMode, setMaskByMode] = useState<Record<Mode, { expand: number; feather: number }>>(defaultMaskByMode)
  const [maskExpandPx, setMaskExpandPx] = useState<number>(defaultMaskByMode['lingerie-cover'].expand)
  const [maskFeatherPx, setMaskFeatherPx] = useState<number>(defaultMaskByMode['lingerie-cover'].feather)

  const [rectCount, setRectCount] = useState<number>(2)
  const [rectangles, setRectangles] = useState<RectDef[]>(defaultRectangles(2))
  const overlayImgRef = useRef<HTMLImageElement | null>(null)
  const [overlaySize, setOverlaySize] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
  const overlaySizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 })
  const dragRef = useRef<
    | null
    | {
        id: string
        kind: 'move' | 'resize'
        handle?: 'nw' | 'ne' | 'sw' | 'se'
        startX: number
        startY: number
        startRect: RectDef
      }
  >(null)

  const [loadingCover, setLoadingCover] = useState<boolean>(false)
  const [loadingUncover, setLoadingUncover] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const [preparedImageB64, setPreparedImageB64] = useState<string>('') // base64 PNG (no data URI)
  const [maskImageB64, setMaskImageB64] = useState<string>('') // base64 PNG (no data URI)
  const [promptResults, setPromptResults] = useState<PromptResult[]>([])

  const [finalImageB64, setFinalImageB64] = useState<string>('') // base64 PNG (no data URI)

  const preparedPreview = preparedImageB64 ? toDataUrlPng(preparedImageB64) : ''
  const maskPreview = maskImageB64 ? toDataUrlPng(maskImageB64) : ''
  const finalPreview = finalImageB64 ? toDataUrlPng(finalImageB64) : ''

  useEffect(() => {
    overlaySizeRef.current = overlaySize
  }, [overlaySize])

  useEffect(() => {
    setMaskExpandPx(maskByMode[mode].expand)
    setMaskFeatherPx(maskByMode[mode].feather)
  }, [maskByMode, mode])

  useEffect(() => {
    const update = () => {
      const el = overlayImgRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setOverlaySize({ w: Math.max(0, Math.round(r.width)), h: Math.max(0, Math.round(r.height)) })
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [originalImage])

  useEffect(() => {
    const onMove = (ev: PointerEvent) => {
      const drag = dragRef.current
      if (!drag) return
      const os = overlaySizeRef.current
      if (!os.w || !os.h) return

      const dx = (ev.clientX - drag.startX) / os.w
      const dy = (ev.clientY - drag.startY) / os.h
      const minSize = 0.02

      setRectangles((prev) =>
        prev.map((r) => {
          if (r.id !== drag.id) return r
          let x = drag.startRect.x
          let y = drag.startRect.y
          let w = drag.startRect.w
          let h = drag.startRect.h

          if (drag.kind === 'move') {
            x = clamp01(x + dx)
            y = clamp01(y + dy)
          } else {
            const handle = drag.handle || 'se'
            if (handle === 'se') {
              w = w + dx
              h = h + dy
            } else if (handle === 'sw') {
              x = x + dx
              w = w - dx
              h = h + dy
            } else if (handle === 'ne') {
              y = y + dy
              w = w + dx
              h = h - dy
            } else if (handle === 'nw') {
              x = x + dx
              y = y + dy
              w = w - dx
              h = h - dy
            }
            w = Math.max(minSize, w)
            h = Math.max(minSize, h)
          }

          x = clamp(x, 0, 1 - w)
          y = clamp(y, 0, 1 - h)
          w = clamp(w, minSize, 1 - x)
          h = clamp(h, minSize, 1 - y)
          return { ...r, x, y, w, h }
        })
      )
    }

    const onEnd = () => {
      dragRef.current = null
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onEnd)
    window.addEventListener('pointercancel', onEnd)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onEnd)
      window.removeEventListener('pointercancel', onEnd)
    }
  }, [])

  useEffect(() => {
    if (mode !== 'rectangles') return
    setRectangles((prev) => {
      const target = clamp(Math.floor(rectCount || 0), 0, 3)
      if (prev.length === target) return prev
      if (prev.length > target) return prev.slice(0, target)
      const next = [...prev]
      const defaults = defaultRectangles(3)
      while (next.length < target) {
        next.push(defaults[next.length])
      }
      return next
    })
  }, [mode, rectCount])

  const handleOriginalUpload = (imageData: string) => {
    setOriginalImage(imageData)
    setFixedImage('')
    setPreparedImageB64('')
    setMaskImageB64('')
    setFinalImageB64('')
    setPromptResults([])
    setError('')
  }

  const handleFixedUpload = (imageData: string) => {
    setFixedImage(imageData)
    setFinalImageB64('')
    setError('')
  }

  const handleCover = async () => {
    if (!originalImage) {
      setError('Wgraj obraz wejściowy (oryginał)')
      return
    }
    if (mode !== 'rectangles') {
      if (!prompts.length) {
        setError('Wpisz przynajmniej jeden prompt (np. "bra", "panties")')
        return
      }
    } else {
      if (!rectangles.length) {
        setError('Dodaj przynajmniej jeden prostokąt')
        return
      }
    }
    if (!/^#([0-9a-fA-F]{6})$/.test(color.trim())) {
      setError('Kolor musi być w formacie HEX, np. #000000')
      return
    }

    setLoadingCover(true)
    setError('')
    setPreparedImageB64('')
    setMaskImageB64('')
    setFinalImageB64('')
    setPromptResults([])

    try {
      const token = localStorage.getItem('token')
      const url =
        mode === 'keep-only'
          ? '/api/photo-tools/retouch/keep'
          : mode === 'rectangles'
            ? '/api/photo-tools/lingerie/rectangles/cover'
            : '/api/photo-tools/lingerie/cover'

      const body =
        mode === 'keep-only'
          ? {
              image: originalImage,
              engine: 'sam3',
              prompts,
              allow_partial: allowPartial,
              mask: { expand_px: maskExpandPx, feather_px: maskFeatherPx },
              background: { color, alpha }
            }
          : mode === 'rectangles'
            ? {
                image: originalImage,
                rectangles,
                mask: { expand_px: maskExpandPx, feather_px: maskFeatherPx },
                cover: { color, alpha }
              }
            : {
                image: originalImage,
                engine: 'sam3',
                prompts,
                allow_partial: allowPartial,
                mask: { expand_px: maskExpandPx, feather_px: maskFeatherPx },
                cover: { color, alpha }
              }
      const resp = await axios.post(url, body, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (resp.data?.status !== 'success') {
        setError(resp.data?.error || 'Nie udało się wykonać Cover')
        return
      }

      setPreparedImageB64(
        normalizeDataUrlToB64(mode === 'keep-only' ? resp.data.keep_image || '' : resp.data.covered_image || '')
      )
      setMaskImageB64(normalizeDataUrlToB64(resp.data.combined_mask_image || ''))
      setPromptResults(resp.data.prompt_results || [])
    } catch (e: any) {
      console.error('Lingerie cover error:', e)
      setError(e.response?.data?.error || 'Wystąpił błąd podczas Cover')
    } finally {
      setLoadingCover(false)
    }
  }

  const handleUncover = async () => {
    if (!originalImage) {
      setError('Wgraj obraz wejściowy (oryginał)')
      return
    }
    if (mode !== 'rectangles') {
      if (!maskImageB64) {
        setError('Najpierw wykonaj Cover (żeby powstała maska)')
        return
      }
    } else {
      if (!rectangles.length) {
        setError('Brak prostokątów')
        return
      }
    }
    if (!fixedImage) {
      setError('Wgraj obraz "fixed" (po Strawberry Pro)')
      return
    }

    setLoadingUncover(true)
    setError('')
    setFinalImageB64('')

    try {
      const token = localStorage.getItem('token')
      const url =
        mode === 'keep-only'
          ? '/api/photo-tools/retouch/apply'
          : mode === 'rectangles'
            ? '/api/photo-tools/lingerie/rectangles/uncover'
            : '/api/photo-tools/lingerie/uncover'

      const payload =
        mode === 'rectangles'
          ? {
              original_image: originalImage,
              fixed_image: fixedImage,
              rectangles,
              mask: { expand_px: maskExpandPx, feather_px: maskFeatherPx }
            }
          : {
              original_image: originalImage,
              fixed_image: fixedImage,
              mask_image: toDataUrlPng(maskImageB64),
              mask: { expand_px: maskExpandPx, feather_px: maskFeatherPx }
            }

      const resp = await axios.post(url, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (resp.data?.status !== 'success') {
        setError(resp.data?.error || 'Nie udało się wykonać Uncover')
        return
      }

      setFinalImageB64(normalizeDataUrlToB64(resp.data.final_image || ''))
    } catch (e: any) {
      console.error('Lingerie uncover error:', e)
      setError(e.response?.data?.error || 'Wystąpił błąd podczas Uncover')
    } finally {
      setLoadingUncover(false)
    }
  }

  const downloadDataUrl = (dataUrl: string, filename: string) => {
    if (!dataUrl) return
    const a = document.createElement('a')
    a.href = dataUrl
    a.download = filename
    a.click()
  }

  const handleReset = () => {
    setOriginalImage('')
    setFixedImage('')
    setPreparedImageB64('')
    setMaskImageB64('')
    setFinalImageB64('')
    setPromptResults([])
    setPromptsText(defaultPromptsForMode(mode))
    setAllowPartial(true)
    setAlpha(255)
    setColor('#000000')
    setMaskByMode(defaultMaskByMode)
    setMaskExpandPx(defaultMaskByMode[mode].expand)
    setMaskFeatherPx(defaultMaskByMode[mode].feather)
    setRectCount(2)
    setRectangles(defaultRectangles(2))
    setError('')
  }

  const beginDrag = (
    e: React.PointerEvent<HTMLDivElement>,
    id: string,
    kind: 'move' | 'resize',
    handle?: 'nw' | 'ne' | 'sw' | 'se'
  ) => {
    const r = rectangles.find((rr) => rr.id === id)
    if (!r) return
    dragRef.current = { id, kind, handle, startX: e.clientX, startY: e.clientY, startRect: r }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              1) Oryginał
            </Typography>
            <ImageUploader label="Oryginał" onImageUpload={(img) => handleOriginalUpload(img)} currentImage={originalImage} />

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                3) Obraz po poprawie (Strawberry Pro)
              </Typography>
              <ImageUploader label="Fixed" onImageUpload={(img) => handleFixedUpload(img)} currentImage={fixedImage} disabled={!maskImageB64} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Wgraj wynik poprawy wykonanej na obrazie{' '}
                <b>{mode === 'keep-only' ? 'keep-only' : 'covered'}</b>.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              2){' '}
              {mode === 'keep-only'
                ? 'Keep-only (SAM3 + union masek)'
                : mode === 'rectangles'
                  ? 'Cover (czarne prostokąty)'
                  : 'Cover (SAM3 + union masek)'}
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Tryb</InputLabel>
              <Select
                value={mode}
                label="Tryb"
                onChange={(e) => {
                  const next = e.target.value as Mode
                  setMode(next)
                  setPromptsText(defaultPromptsForMode(next))
                  setPreparedImageB64('')
                  setMaskImageB64('')
                  setFinalImageB64('')
                  setPromptResults([])
                  setError('')
                }}
              >
                <MenuItem value="lingerie-cover">Zakryj bieliznę (bra + panties + torso)</MenuItem>
                <MenuItem value="rectangles">Zakryj bieliznę (czarne prostokąty)</MenuItem>
                <MenuItem value="keep-only">Zostaw twarz/dłonie/stopy (face + head + hands + feet)</MenuItem>
              </Select>
            </FormControl>

            {mode !== 'rectangles' && (
              <TextField
                fullWidth
                label="Prompts (po jednym na linię lub po przecinku)"
                value={promptsText}
                onChange={(e) => setPromptsText(e.target.value)}
                size="small"
                multiline
                minRows={2}
                sx={{ mb: 2 }}
                placeholder={defaultPromptsForMode(mode)}
              />
            )}

            {mode === 'rectangles' && (
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Prostokąty</InputLabel>
                      <Select
                        value={rectCount}
                        label="Prostokąty"
                        onChange={(e) => setRectCount(Number(e.target.value))}
                      >
                        <MenuItem value={1}>1</MenuItem>
                        <MenuItem value={2}>2</MenuItem>
                        <MenuItem value={3}>3</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setRectangles(defaultRectangles(rectCount))
                        setError('')
                      }}
                    >
                      Reset prostokątów
                    </Button>
                  </Grid>
                </Grid>

                {originalImage ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                      <img
                        ref={overlayImgRef}
                        src={originalImage}
                        alt="original-overlay"
                        style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain', display: 'block' }}
                        onLoad={() => {
                          const el = overlayImgRef.current
                          if (!el) return
                          const r = el.getBoundingClientRect()
                          setOverlaySize({ w: Math.max(0, Math.round(r.width)), h: Math.max(0, Math.round(r.height)) })
                        }}
                      />
                      {overlaySize.w > 0 &&
                        overlaySize.h > 0 &&
                        rectangles.map((r) => (
                          <Box
                            key={r.id}
                            onPointerDown={(e) => beginDrag(e as any, r.id, 'move')}
                            sx={{
                              position: 'absolute',
                              left: `${r.x * 100}%`,
                              top: `${r.y * 100}%`,
                              width: `${r.w * 100}%`,
                              height: `${r.h * 100}%`,
                              backgroundColor: 'rgba(0,0,0,0.35)',
                              border: '2px solid rgba(255,255,255,0.9)',
                              boxSizing: 'border-box',
                              cursor: 'move'
                            }}
                          >
                            {(['nw', 'ne', 'sw', 'se'] as const).map((hnd) => (
                              <Box
                                key={hnd}
                                onPointerDown={(e) => {
                                  e.stopPropagation()
                                  beginDrag(e as any, r.id, 'resize', hnd)
                                }}
                                sx={{
                                  position: 'absolute',
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  border: '1px solid rgba(0,0,0,0.5)',
                                  cursor: `${hnd}-resize`,
                                  left: hnd.includes('w') ? -6 : 'calc(100% - 6px)',
                                  top: hnd.includes('n') ? -6 : 'calc(100% - 6px)'
                                }}
                              />
                            ))}
                          </Box>
                        ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Wgraj najpierw oryginał, aby ustawić prostokąty.
                  </Typography>
                )}
              </Box>
            )}

            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Kolor"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  size="small"
                  placeholder="#000000"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {mode === 'keep-only' ? `Tło (alpha): ${alpha}` : `Krycie (alpha): ${alpha}`}
                </Typography>
                <Slider
                  value={alpha}
                  onChange={(_, v) => setAlpha(v as number)}
                  min={0}
                  max={255}
                  step={1}
                  valueLabelDisplay="auto"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Powiększenie maski (px): {maskExpandPx}
                </Typography>
                <Slider
                  value={maskExpandPx}
                  onChange={(_, v) => {
                    const next = v as number
                    setMaskExpandPx(next)
                    setMaskByMode((prev) => ({ ...prev, [mode]: { ...prev[mode], expand: next } }))
                  }}
                  min={0}
                  max={64}
                  step={1}
                  valueLabelDisplay="auto"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Feather (px): {maskFeatherPx}
                </Typography>
                <Slider
                  value={maskFeatherPx}
                  onChange={(_, v) => {
                    const next = v as number
                    setMaskFeatherPx(next)
                    setMaskByMode((prev) => ({ ...prev, [mode]: { ...prev[mode], feather: next } }))
                  }}
                  min={0}
                  max={32}
                  step={1}
                  valueLabelDisplay="auto"
                />
              </Grid>
            </Grid>

            {mode === 'rectangles' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Tip: jeśli widać czarne łączenie, zwiększ <b>Feather</b> (np. 2–6) i/lub <b>Powiększenie maski</b>.
              </Typography>
            )}

            {mode !== 'rectangles' && (
              <FormControlLabel
                control={<Switch checked={allowPartial} onChange={(e) => setAllowPartial(e.target.checked)} />}
                label="Zezwól na częściowy sukces (gdy jeden prompt nie zadziała)"
                sx={{ mb: 2 }}
              />
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={loadingCover ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
                onClick={handleCover}
                disabled={loadingCover || !originalImage}
              >
                {mode === 'keep-only' ? 'Keep' : 'Cover'}
              </Button>
              <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset}>
                Reset
              </Button>
            </Box>

            {promptResults.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Wyniki promptów:
                </Typography>
                {promptResults.map((r, idx) => (
                  <Typography key={`${r.prompt}-${idx}`} variant="body2" color={r.status === 'success' ? 'success.main' : 'error.main'}>
                    - {r.prompt}: {r.status === 'success' ? 'OK' : `ERROR${r.status === 'error' && (r as any).error ? `: ${(r as any).error}` : ''}`}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Podgląd: {mode === 'keep-only' ? 'Keep-only + maska' : 'Covered + maska'}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {preparedPreview ? (
                  <Box>
                    <img src={preparedPreview} alt="prepared" style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain' }} />
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadDataUrl(preparedPreview, mode === 'keep-only' ? 'keep.png' : 'covered.png')}
                      >
                        Pobierz {mode === 'keep-only' ? 'keep' : 'covered'}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Brak {mode === 'keep-only' ? 'keep-only' : 'covered'} (kliknij {mode === 'keep-only' ? 'Keep' : 'Cover'})
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {maskPreview ? (
                  <Box>
                    <img src={maskPreview} alt="mask" style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain' }} />
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadDataUrl(maskPreview, 'mask.png')}>
                        Pobierz maskę
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Brak maski (kliknij Cover)
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              4) {mode === 'keep-only' ? 'Apply (wklej poprawki na oryginał)' : 'Uncover (przywróć bieliznę z oryginału)'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={loadingUncover ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
                onClick={handleUncover}
                disabled={loadingUncover || !originalImage || !maskImageB64 || !fixedImage}
              >
                {mode === 'keep-only' ? 'Apply' : 'Uncover'}
              </Button>
              {finalPreview && (
                <Button startIcon={<DownloadIcon />} onClick={() => downloadDataUrl(finalPreview, 'final_uncovered.png')}>
                  Pobierz final
                </Button>
              )}
            </Box>

            {finalPreview ? (
              <img src={finalPreview} alt="final" style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain' }} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Brak wyniku (kliknij Uncover)
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default LingerieFixTool
